import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AccountResponse, CreateSubscriptionResponse, EmailChangeData, PasswordChangeData, ResetPasswordData, SubscriptionResponse } from './types';
import { LoginData } from '../auth/types';

export const API_URL = import.meta.env.VITE_JUSTWORDS_API_URL.replace("v1", "v2");

export const api = createApi({
  reducerPath: 'account',
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    credentials: 'include'
  }),
  refetchOnMountOrArgChange: 30,
  tagTypes: ['account', 'subscription'],
  endpoints: builder => ({
    getAccount: builder.query<AccountResponse, void>({
      query: () => `account`,
      providesTags: ['account']
    }),
    getSubscription: builder.query<SubscriptionResponse, void>({
      query: () => `subscription`,
      providesTags: ['subscription']
    }),
    logIn: builder.mutation<void, LoginData>({
      query: (credentials) => ({
        url: 'authenticate',
        method: 'POST',
        body: credentials
      }),
      invalidatesTags: ['account', 'subscription']
    }),
    logOut: builder.mutation<void, void>({
      query: () => ({
        url: 'authenticate',
        method: 'DELETE'
      }),
      invalidatesTags: ['account', 'subscription']
    }),
    createSubscription: builder.mutation<CreateSubscriptionResponse, string>({
      query: (token) => ({
        url: 'subscription',
        method: 'PUT',
        body: {
          token
        }
      }),
      invalidatesTags: ['account', 'subscription']
    }),
    cancelSubscription: builder.mutation<SubscriptionResponse, void>({
      query: () => ({
        url: 'subscription',
        method: 'DELETE'
      }),
      invalidatesTags: ['account', 'subscription']
    }),
    changeEmail: builder.mutation<void, EmailChangeData>({
      query: (data) => ({
        url: 'email/change',
        method: 'POST',
        body: data
      }),
      async onQueryStarted({ email }, { dispatch, queryFulfilled }) {
        // Optimistic update of our stored account state
        dispatch(
          api.util.updateQueryData('getAccount', undefined, (draft) => {
            Object.assign(draft, { email });
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          dispatch(api.util.invalidateTags(['account']));
        }
      },
    }),
    changePassword: builder.mutation<void, PasswordChangeData>({
      query: (data) => ({
        url: 'password/change',
        method: 'POST',
        body: data
      })
    }),
    resetPassword: builder.mutation<void, ResetPasswordData>({
      query: (data) => ({
        url: 'password/reset',
        method: 'POST',
        body: data
      })
    })
  })
});

export const accountStateSelector = api.endpoints.getAccount.select();
